import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { FormWrapper } from '@/form-control'
import { TextField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IUser } from '@/MODELS/user.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'
import {
    ageOptions,
    birthYearOptions,
    communicationLevelOptions,
    currentDiagnoseStepOptions,
    diagnosisOptions,
    genderOptions,
    guardianRoleOptions,
    mainConcernOptions,
    monthOptions,
    SERVICES_CONSENT_LABEL,
    servicesReceivedOptions,
    supportNeedsOptions,
    userGoalsAsAParentOptions,
    userGoalsForChildOptions,
} from '@/utils/options'

import InteractiveLoading from '@/components/InteractiveLoading'
import { SectionHeader } from '@/components/SectionHeader'

function NativeSelect({ value, onChange, options, placeholder }: {
    value: string
    onChange: (value: string) => void
    options: { value: string; text: string }[]
    placeholder?: string
}) {
    return (
        <select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className='field-input'
        >
            {placeholder && <option value='' disabled>{placeholder}</option>}
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.text}</option>
            ))}
        </select>
    )
}

function CheckboxGroup({ values, onChange, options }: {
    values: string[]
    onChange: (values: string[]) => void
    options: { value: string; text: string }[]
}) {
    function toggle(optionValue: string) {
        const current = values ?? []
        const next = current.includes(optionValue)
            ? current.filter((v) => v !== optionValue)
            : [...current, optionValue]
        onChange(next)
    }

    return (
        <div className='space-y-2'>
            {options.map((opt) => (
                <label key={opt.value} className='flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50'>
                    <input
                        type='checkbox'
                        checked={(values ?? []).includes(opt.value)}
                        onChange={() => toggle(opt.value)}
                        className='w-4 h-4 rounded text-brand-600 border-gray-300 focus:ring-brand-500'
                    />
                    <span className='text-sm text-gray-700'>{opt.text}</span>
                </label>
            ))}
        </div>
    )
}

export default function UserEditProfileForm() {
    const [showRegenerateModal, setShowRegenerateModal] = useState(false)
    const [isRegenerating, setIsRegenerating] = useState(false)
    const { user } = useAuth()
    const navigate = useNavigate()
    const formSubmitRef = { current: null as (() => Promise<void>) | null }

    async function regenerateMilestones() {
        setShowRegenerateModal(false)
        setIsRegenerating(true)
        try {
            // Auto-save profile before regenerating so the roadmap uses the latest data
            if (formSubmitRef.current) {
                await formSubmitRef.current()
            }
            // Same layered timeout as onboarding: 120s frontend → 60s OpenAI SDK → retry with gpt-4.1-nano
            const x = await api(ROUTES.app.regenerateRoadMap, {}, false, 120_000)
            if (x.status === 200) {
                setIsRegenerating(false)
                navigate('/roadmap')
            }
        } catch (err) {
            console.error('Error regenerating user roadmap ')
            setIsRegenerating(false)
        }
    }

    return (
        <div className='flex-1 w-full h-screen pb-8 mx-auto '>
            <SectionHeader title={'Edit Profile'} />

            {isRegenerating ? (
                <div className='flex flex-col items-center justify-center h-64'>
                    <div className='flex flex-col items-center justify-center'>
                        <InteractiveLoading showProgress={false} />
                    </div>
                </div>
            ) : (
                <div className='p-6'>
                    <div className='w-full p-6 bg-white border border-gray-100 rounded-lg shadow-sm '>
                        <FormWrapper<IUser>
                            endpoint={ROUTES.app.user}
                            id={user?._id}
                            submitButtonClass='w-full px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 '
                            submitButtonText='Save Changes'
                        >
                            {(f, { formValues, setFormValues, submit }) => {
                                formSubmitRef.current = submit
                                const ui = formValues.userInfo ?? {} as any
                                const setUserInfo = (field: string, value: any) => {
                                    setFormValues((prev: any) => ({
                                        ...prev,
                                        userInfo: { ...prev.userInfo, [field]: value },
                                    }))
                                }

                                return (
                                <div className='space-y-8'>
                                    <div>
                                        <h3 className='mb-4 text-lg font-medium text-gray-900'>Personal Information</h3>
                                        <div className='space-y-4'>
                                            <TextField
                                                {...f('name')}
                                                label='Full Name'
                                                placeholder='Enter your full name'
                                                className='w-full'
                                            />
                                        </div>
                                    </div>

                                    {formValues.userInfo && (
                                        <>
                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Child Information
                                                </h3>
                                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                                    <TextField
                                                        {...f('userInfo.childName')}
                                                        label="Child's Name"
                                                        placeholder="Enter your child's name"
                                                        className='w-full'
                                                    />
                                                    <div className='field-container md:col-span-2'>
                                                        <label className='field-label'>Child's Date of Birth</label>
                                                        <p className='text-xs text-gray-500 mb-2'>Month and year only</p>
                                                        <div className='grid grid-cols-2 gap-2'>
                                                            <NativeSelect
                                                                value={ui.childBirthMonth ? String(ui.childBirthMonth) : ''}
                                                                onChange={(v) => {
                                                                    const month = v ? parseInt(v, 10) : undefined
                                                                    setUserInfo('childBirthMonth', month)
                                                                }}
                                                                options={monthOptions}
                                                                placeholder='Month'
                                                            />
                                                            <NativeSelect
                                                                value={ui.childBirthYear ? String(ui.childBirthYear) : ''}
                                                                onChange={(v) => {
                                                                    const year = v ? parseInt(v, 10) : undefined
                                                                    setUserInfo('childBirthYear', year)
                                                                }}
                                                                options={birthYearOptions}
                                                                placeholder='Year'
                                                            />
                                                        </div>
                                                        {!ui.childBirthMonth && !ui.childBirthYear && ui.childAge && (
                                                            <p className='text-xs text-gray-500 mt-1'>
                                                                Currently using age range: {ageOptions.find((o) => o.value === ui.childAge)?.text}. Add a date of birth for more precise personalization.
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className='field-container'>
                                                        <label className='field-label'>Child's Gender</label>
                                                        <NativeSelect
                                                            value={ui.childGender}
                                                            onChange={(v) => setUserInfo('childGender', v)}
                                                            options={genderOptions}
                                                            placeholder='Select gender...'
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Communication & Support
                                                </h3>
                                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                                    <div className='field-container'>
                                                        <label className='field-label'>Communication Level</label>
                                                        <NativeSelect
                                                            value={ui.communicationLevel}
                                                            onChange={(v) => setUserInfo('communicationLevel', v)}
                                                            options={communicationLevelOptions}
                                                            placeholder='Select level...'
                                                        />
                                                    </div>
                                                    <div className='field-container'>
                                                        <label className='field-label'>Daily Support Needs</label>
                                                        <NativeSelect
                                                            value={ui.supportNeeds}
                                                            onChange={(v) => setUserInfo('supportNeeds', v)}
                                                            options={supportNeedsOptions}
                                                            placeholder='Select support level...'
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Guardian Information
                                                </h3>
                                                <div className='space-y-4'>
                                                    <div className='field-container'>
                                                        <label className='field-label'>Your Role</label>
                                                        <NativeSelect
                                                            value={ui.guardianRole}
                                                            onChange={(v) => setUserInfo('guardianRole', v)}
                                                            options={guardianRoleOptions}
                                                            placeholder='Select role...'
                                                        />
                                                    </div>
                                                    {ui.guardianRole === 'other' && (
                                                        <TextField
                                                            {...f('userInfo.guardianRoleOther')}
                                                            label='Specify Other Role'
                                                            placeholder='Please specify your role'
                                                            className='w-full'
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Main Concerns
                                                </h3>
                                                <div className='space-y-4'>
                                                    <label className='field-label'>What are your main concerns? (Select all that apply)</label>
                                                    <CheckboxGroup
                                                        values={ui.mainConcerns}
                                                        onChange={(v) => setUserInfo('mainConcerns', v)}
                                                        options={mainConcernOptions}
                                                    />
                                                    {ui.mainConcerns?.includes('other') && (
                                                        <TextField
                                                            {...f('userInfo.mainConcernsOther')}
                                                            label='Other Concerns'
                                                            placeholder='Please specify other concerns'
                                                            className='w-full'
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Diagnosis Information
                                                </h3>
                                                <div className='space-y-4'>
                                                    <div className='field-container'>
                                                        <label className='field-label'>Current Diagnosis Step</label>
                                                        <NativeSelect
                                                            value={ui.currentDiagnoseStep}
                                                            onChange={(v) => setUserInfo('currentDiagnoseStep', v)}
                                                            options={currentDiagnoseStepOptions}
                                                            placeholder='Select step...'
                                                        />
                                                    </div>
                                                    <label className='field-label'>Diagnosis (Select all that apply)</label>
                                                    <CheckboxGroup
                                                        values={ui.diagnosisInformation}
                                                        onChange={(v) => setUserInfo('diagnosisInformation', v)}
                                                        options={diagnosisOptions}
                                                    />
                                                    {(ui.diagnosisInformation ?? []).includes('other') && (
                                                        <TextField
                                                            {...f('userInfo.diagnosisInformationOther')}
                                                            label='Other Diagnosis Information'
                                                            placeholder='Please specify the diagnosis'
                                                            className='w-full'
                                                        />
                                                    )}
                                                    <TextField
                                                        {...f('userInfo.waitlistSignUpDate')}
                                                        label='Waitlist Sign-up Date'
                                                        type='date'
                                                        className='w-full'
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Services Currently Received
                                                </h3>
                                                <div className='space-y-4'>
                                                    <label className='flex items-start gap-3 px-3 py-3 rounded-lg cursor-pointer bg-gray-50 border border-gray-200'>
                                                        <input
                                                            type='checkbox'
                                                            checked={!!ui.servicesReceivedConsent}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked
                                                                setFormValues((prev: any) => ({
                                                                    ...prev,
                                                                    userInfo: {
                                                                        ...prev.userInfo,
                                                                        servicesReceivedConsent: checked,
                                                                        servicesReceived: checked ? prev.userInfo?.servicesReceived ?? [] : [],
                                                                    },
                                                                }))
                                                            }}
                                                            className='mt-1 w-4 h-4 rounded text-brand-600 border-gray-300 focus:ring-brand-500'
                                                        />
                                                        <span className='text-sm text-gray-700 leading-snug'>{SERVICES_CONSENT_LABEL}</span>
                                                    </label>
                                                    {ui.servicesReceivedConsent && (
                                                        <>
                                                            <label className='field-label'>Select all that apply</label>
                                                            <CheckboxGroup
                                                                values={ui.servicesReceived}
                                                                onChange={(v) => setUserInfo('servicesReceived', v)}
                                                                options={servicesReceivedOptions}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Goals for Child
                                                </h3>
                                                <div className='space-y-4'>
                                                    <label className='field-label'>What are your goals for your child? (Select all that apply)</label>
                                                    <CheckboxGroup
                                                        values={ui.userGoalsForChild}
                                                        onChange={(v) => setUserInfo('userGoalsForChild', v)}
                                                        options={userGoalsForChildOptions}
                                                    />
                                                    {ui.userGoalsForChild?.includes('other') && (
                                                        <TextField
                                                            {...f('userInfo.userGoalsForChildOther')}
                                                            label='Other Goals for Child'
                                                            placeholder='Please specify other goals'
                                                            className='w-full'
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className='mb-4 text-lg font-medium text-gray-900'>
                                                    Your Goals as a Caregiver
                                                </h3>
                                                <div className='space-y-4'>
                                                    <label className='field-label'>What are your goals as a caregiver? (Select all that apply)</label>
                                                    <CheckboxGroup
                                                        values={ui.userGoalsCareGiver}
                                                        onChange={(v) => setUserInfo('userGoalsCareGiver', v)}
                                                        options={userGoalsAsAParentOptions}
                                                    />
                                                    {ui.userGoalsCareGiver?.includes('other') && (
                                                        <TextField
                                                            {...f('userInfo.userGoalsCareGiverOther')}
                                                            label='Other Caregiver Goals'
                                                            placeholder='Please specify other goals'
                                                            className='w-full'
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                )
                            }}
                        </FormWrapper>
                        <button
                            type='button'
                            onClick={() => setShowRegenerateModal(true)}
                            className='w-full mb-24 px-6 py-3 my-5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transform hover:scale-[1.02] transition-all duration-200 shadow-md hover:shadow-lg'
                        >
                            <span className='flex items-center justify-center'>
                                {<RefreshCw className='w-4 h-4 mr-2' />}
                                Regenerate Roadmap
                            </span>
                        </button>
                    </div>

                    {showRegenerateModal && (
                        <RegenerateModal
                            regenerateMilestones={regenerateMilestones}
                            setShowRegenerateModal={setShowRegenerateModal}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface IRegenerateModal {
    setShowRegenerateModal: (show: boolean) => void
    regenerateMilestones: () => void
}

function RegenerateModal(props: IRegenerateModal) {
    const { setShowRegenerateModal, regenerateMilestones } = props
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
            <div className='w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl'>
                <div className='flex items-center gap-3 mb-4'>
                    <div className='flex items-center justify-center w-10 h-10 rounded-full bg-brand-100'>
                        <AlertTriangle className='w-5 h-5 text-brand-600' />
                    </div>
                    <h3 className='text-lg font-semibold text-gray-900'>Regenerate Roadmap</h3>
                </div>

                <div className='mb-6'>
                    <p className='text-sm text-gray-600'>
                        Are you sure you want to regenerate the roadmap? This action will:
                    </p>
                    <ul className='mt-2 ml-4 text-sm text-gray-600 list-disc'>
                        <li>Overwrite all existing milestones</li>
                        <li>Reset all progress tracking</li>
                        <li>Create a new roadmap based on updated information</li>
                    </ul>
                    <p className='mt-3 text-sm font-medium text-orange-600'>
                        This action cannot be undone. Your profile changes will be saved automatically before regenerating.
                    </p>
                </div>

                <div className='flex gap-3'>
                    <button
                        onClick={() => setShowRegenerateModal(false)}
                        className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => regenerateMilestones()}
                        className='flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700'
                    >
                        Regenerate
                    </button>
                </div>
            </div>
        </div>
    )
}
